import { useParams, Link } from "react-router-dom"
import { useState, useEffect, useMemo } from "react"
import { useStoreDetail } from "@/graphql/hooks/useStoreDetail"
import { useTournamentList } from "@/graphql/hooks/useTournamentList"
import { useUrlPagination } from "@/hooks/useUrlPagination"
import Layout from "@/components/Layout/Layout"
import { TournamentCard } from "@/components/TournamentCard"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { ArrowLeft, Search, Filter, Trophy, ActivitySquareIcon, ArchiveIcon } from "lucide-react"
import { RenderSimplePagination } from "./Tiendas"
import { useAllGamesWithSeasons } from "@/graphql/hooks/useGames"
import { useTournamentTypesListPlain } from "@/graphql/hooks/useTournamentTypesList"
import { useAuthContext } from "@/contexts/AuthContext"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"

const randomPlaceholderLogo = (id: number) => {
  const placeholders = [1, 2, 3, 4, 5] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length]
  return `/img/placeholder-logo-tienda${index}.png`
}

const randomPlaceholderCover = (id: number) => {
  const placeholders = [1, 2, 3] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length] // mapea a 1, 2 o 3
  return `/img/placeholder-tienda${index}.png`
}

const statusOptions = [
  { value: 1, label: "Programado" },
  { value: 2, label: "Inscripciones" },
  { value: 3, label: "Iniciado" },
  { value: 4, label: "Proceso" },
  { value: 5, label: "Terminado" },
  { value: 6, label: "Reportado" },
]

const StoreTournaments = () => {
  const { storeId, slug } = useParams()
  const { isAdmin, isAuthenticated } = useAuthContext()
  const { currentPage, setPage } = useUrlPagination(1)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")
  const [inputValue, setInputValue] = useState<string>("")
  const [selectedGame, setSelectedGame] = useState<string>("")
  const [selectedType, setSelectedType] = useState<string>("")
  const [selectedSeason, setSelectedSeason] = useState<string>("")
  const [statusIds, setStatusIds] = useState<number[]>([])
  const { games, loading: gamesLoading } = useAllGamesWithSeasons(true, {
    isAdmin,
    isAuthenticated,
  })
  const { types, loading: typesLoading } = useTournamentTypesListPlain()

  const allSeasons = useMemo(() => {
    const seasons = games
      .filter((g) => g.id === Number(selectedGame))
      .flatMap((g) => {
        return g.Seasons || []
      })
    return seasons
      .filter((s, i, self) => i === self.findIndex((ss) => ss.id === s.id))
      .sort((a, b) => b.id - a.id)
  }, [games, selectedGame])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue)
    }, 500)

    return () => clearTimeout(timer)
  }, [inputValue])

  // Update searchTerm when debouncedSearchTerm changes
  useEffect(() => {
    setSearchTerm(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  const { store, loading: storeLoading } = useStoreDetail(parseInt(storeId || "0"))

  const {
    tournaments,
    hasNextPage,
    loading: tournamentsLoading,
  } = useTournamentList({
    input: {
      page: currentPage,
      limit: 12,
      storeId: parseInt(storeId || "0"),
      typeId: selectedType ? parseInt(selectedType) : 0,
      search: searchTerm,
      seasonId: selectedSeason ? parseInt(selectedSeason) : 0,
      gameId: selectedGame ? parseInt(selectedGame) : 0,
      statusId: statusIds,
    },
  })

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDebouncedSearchTerm(inputValue)
    }
  }

  const clearFilters = () => {
    setSelectedGame("")
    setSelectedType("")
    setSelectedSeason("")
    setSearchTerm("")
    setInputValue("")
    setDebouncedSearchTerm("")
    setStatusIds([])
    setPage(1)
  }

  const toggleValue = (value: number) => {
    setStatusIds((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value)
      } else {
        return [...prev, Number(value)]
      }
    })
  }

  if (storeLoading || gamesLoading || typesLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingScreen />
        </div>
      </Layout>
    )
  }

  if (!store) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Tienda no encontrada</h2>
          <Link to="/stores">
            <Button>Volver a Tiendas</Button>
          </Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto px-2 py-8 max-w-7xl">
        <div className="mb-6">
          <div className="w-full rounded-lg overflow-hidden bg-muted">
            <img
              src={store.urlCover || randomPlaceholderCover(Number(storeId))}
              alt={store.name}
              className="w-full h-72 object-cover hidden sm:flex hover:scale-110 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.src = randomPlaceholderCover(Number(storeId))
              }}
            />
          </div>
        </div>
        <div className="mb-6">
          <Link to={`/store/detail/${storeId}/${slug}`}>
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a {store.name}
            </Button>
          </Link>

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6">
            {/* Logo de la tienda */}
            <img
              src={store.urlLogo || randomPlaceholderLogo(Number(storeId))}
              alt={`${store.name} logo`}
              className="hidden sm:flex h-20 sm:h-24 w-auto object-contain rounded-lg border bg-card"
            />

            {/* Información de la tienda */}
            <div className="text-center sm:text-left flex flex-col justify-center">
              {/* Título con ícono */}
              <h1 className="flex items-center justify-center sm:justify-start gap-2 text-xl sm:text-3xl font-bold">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8" />
                Torneos de {store.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <Filter className="w-5 h-5" />
              Filtros de Búsqueda
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar torneo..."
                  value={inputValue}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  className="pl-10 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              {/* Juego */}
              <Select value={selectedGame} onValueChange={setSelectedGame}>
                <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectValue placeholder="Seleccionar juego" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectItem value="all">Todos los juegos</SelectItem>
                  {games.map((game) => (
                    <SelectItem
                      key={game.id}
                      value={game.id.toString()}
                      className={`${game.isActive ? "" : "text-muted-foreground"} cursor-pointer`}
                    >
                      {game.isActive === 1 && (
                        <ActivitySquareIcon className="inline h-4 w-4 mr-2" />
                      )}
                      {game.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Temporada */}
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectValue placeholder="Temporada" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectItem value="all">Todas las temporadas</SelectItem>
                  {allSeasons.map((season) => (
                    <SelectItem key={season.id} value={season.id.toString()}>
                      {season.name}
                      {season.isActive > 0 && (
                        <Badge
                          variant="secondary"
                          className="ml-1 dark:bg-secondary-dark dark:text-secondary-foreground"
                        >
                          Activa
                        </Badge>
                      )}
                      {season.isArchived > 0 && (
                        <Badge
                          variant="outline"
                          className="ml-1 dark:bg-secondary-dark dark:text-secondary-foreground"
                        >
                          <ArchiveIcon className="h-3 w-3 mr-2 inline" />
                          Archivada
                        </Badge>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Tipo */}
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectValue placeholder="Tipo de torneo" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {types.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* MultiSelect Estado */}
              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      {statusIds.length > 0
                        ? `${statusIds.length} seleccionado(s)`
                        : "Todos los estados"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg rounded-lg">
                    {statusOptions.map((status) => {
                      const isChecked = statusIds.includes(status.value)
                      return (
                        <DropdownMenuCheckboxItem
                          key={status.value}
                          checked={isChecked}
                          onCheckedChange={() => toggleValue(status.value)}
                          className="focus:bg-gray-100 dark:focus:bg-gray-700 focus:text-gray-900 dark:focus:text-gray-100"
                        >
                          {status.label}
                        </DropdownMenuCheckboxItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {(selectedGame ||
              selectedType ||
              selectedSeason ||
              searchTerm ||
              statusIds.length > 0) && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Limpiar Filtros
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tournament List */}
        {tournamentsLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingScreen />
          </div>
        ) : tournaments.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {tournaments.map((tournament) => (
                <TournamentCard key={tournament.id} tournament={tournament} />
              ))}
            </div>

            {/* Pagination */}
            <RenderSimplePagination
              hasNextPage={hasNextPage}
              currentPage={currentPage}
              setPage={setPage}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No se encontraron torneos</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || selectedGame || selectedType || selectedSeason
                ? "Intenta ajustar los filtros de búsqueda"
                : "Esta tienda no tiene torneos disponibles en este momento"}
            </p>
            {(searchTerm || selectedGame || selectedType || selectedSeason) && (
              <Button onClick={clearFilters}>Limpiar Filtros</Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default StoreTournaments
